import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, post } from '../../lib/api.js';
import { errorMessage } from '../../lib/passkey.js';
import { Alert, PasswordField } from '../../lib/ui.js';
import { t } from '../../i18n/index.js';

/** Email form; the server's answer is the same whether or not the address exists. */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post('/auth/admin/forgot', { email });
      setSent(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-form">
      <h2>{t('forgot_title')}</h2>
      {sent ? (
        <>
          <Alert kind="info">{t('forgot_sent')}</Alert>
          <p>
            <Link to="/">{t('forgot_back')}</Link>
          </p>
        </>
      ) : (
        <form onSubmit={submit}>
          <p className="muted">{t('forgot_intro')}</p>
          {error && <Alert>{error}</Alert>}
          <label>
            {t('email')}
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
            />
          </label>
          <div className="row">
            <button type="submit" disabled={busy}>
              {t('forgot_send')}
            </button>
            <Link to="/">{t('forgot_back')}</Link>
          </div>
        </form>
      )}
    </div>
  );
}

/** New password for a single-use link; afterwards the normal password + passkey sign-in applies. */
export function ResetPasswordPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const [valid, setValid] = useState<boolean | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<{ valid: boolean }>(`/auth/reset/${token}`)
      .then((r) => setValid(r.valid))
      .catch(() => setValid(false));
  }, [token]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      setError(t('reset_mismatch'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await post<{ ok: boolean; email: string | null }>(`/auth/reset/${token}`, {
        password,
      });
      setDone(r.email ?? '');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-form">
      <h2>{t('reset_title')}</h2>
      {valid === null ? null : done !== null ? (
        <>
          <Alert kind="success">{t('reset_done')}</Alert>
          <button type="button" onClick={() => navigate('/', { state: { email: done } })}>
            {t('reset_go_sign_in')}
          </button>
        </>
      ) : !valid ? (
        <>
          <Alert>{t('reset_invalid')}</Alert>
          <p>
            <Link to="/forgot">{t('admin_login_forgot')}</Link>
          </p>
        </>
      ) : (
        <form onSubmit={submit}>
          <p className="muted">{t('reset_intro')}</p>
          {error && <Alert>{error}</Alert>}
          <PasswordField
            id="reset-password"
            label={t('reset_new')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={12}
            autoComplete="new-password"
          />
          <PasswordField
            id="reset-confirm"
            label={t('reset_confirm')}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={12}
            autoComplete="new-password"
          />
          <button type="submit" disabled={busy}>
            {t('reset_save')}
          </button>
        </form>
      )}
    </div>
  );
}
